import { isValidElement, type ReactNode } from "react";
import type { MenuProps } from "antd";
import { cn } from "@/v2/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/v2/ui/tooltip";

type Item = NonNullable<MenuProps["items"]>[number] & { key?: string; label?: ReactNode; danger?: boolean; disabled?: boolean; title?: string };

// The classic roots label items with MenuItemNotification (icon + text); the pills only need the text
const textOf = (label: ReactNode) => (isValidElement<{ text?: string }>(label) && label.props.text) || label;

/**
 * Service, component and cluster settings in the new UI: the classic roots' menu as pills, the
 * same control as project settings, with the tab at full width below.
 */
export function EntitySettingsTabs({ items, active, onSelect, children }: {
    items: MenuProps["items"];
    active: string;
    onSelect: (key: string) => void;
    children: ReactNode;
}) {
    return (
        <div className="space-y-5">
            <nav aria-label="Settings" className="inline-flex flex-wrap gap-1 rounded-full bg-soft p-1">
                {((items || []) as Item[]).map(item => {
                    const pill = (
                        <button
                            key={String(item.key)}
                            type="button"
                            disabled={item.disabled}
                            aria-current={active === item.key ? "page" : undefined}
                            onClick={() => onSelect(String(item.key))}
                            className={cn(
                                "rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors disabled:cursor-default disabled:opacity-50",
                                active === item.key ? "bg-tile shadow-tile" : "enabled:hover:text-text",
                                item.danger ? "text-bad-ink" : active === item.key ? "text-text" : "text-text-2",
                            )}
                        >
                            {textOf(item.label)}
                        </button>
                    );
                    // A tab that can't be used here says why
                    return item.disabled && item.title ? (
                        <Tooltip key={String(item.key)}>
                            <TooltipTrigger render={<span />}>{pill}</TooltipTrigger>
                            <TooltipContent>{item.title}</TooltipContent>
                        </Tooltip>
                    ) : pill;
                })}
            </nav>
            <div className="min-w-0">{children}</div>
        </div>
    );
}
